import { LegalPage } from '../components/LegalPage';

/**
 * Privacy policy. Drafted for the pilot — accurate to the current
 * architecture (pseudonymous to peers, email-domain verification,
 * append-only audit log, moderation cascade). Edit before the first
 * outside-institution pilot if legal/policy review wants different
 * wording. The page is reachable at /privacy whether signed in or not.
 */
export function Privacy() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="2026-05-23">
      <p>
        LessAlone is a peer-connection app scoped to a single university or workplace community. We
        hold the minimum information needed to verify you belong, pair you with another member, and
        keep the space safe.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Your email address.</strong> We use it to verify you're a member of a supported
          community (a magic-link sign-in) and to send you check-ins. We never display your email to
          another member.
        </li>
        <li>
          <strong>Your nickname and chosen interest tags.</strong> These are what your peer sees.
          You pick them and you can change them.
        </li>
        <li>
          <strong>Your messages.</strong> Stored so a moderator can act on a report and so you can
          read your conversation across sessions.
        </li>
        <li>
          <strong>Limited operational data.</strong> Things like when you last opened the app and
          which devices you've registered for push notifications. Used to keep the service working;
          never sold.
        </li>
      </ul>

      <h2>What other members see</h2>
      <p>
        Only the nickname you chose, the interest tags you chose, and the messages you sent in that
        conversation. Not your real name, not your email, not any other thread you're in. This is a
        hard constraint in our architecture, not a setting.
      </p>

      <h2>Who sees your messages besides your peer</h2>
      <p>
        Trained moderators may review a message if you or your peer reports it, if our automated
        safety checks flag it, or if a crisis signal is detected. Their actions are recorded in an
        append-only audit log. Moderators see the conversation context they need to act, never your
        unrelated threads.
      </p>

      <h2>Crisis signals</h2>
      <p>
        If our moderation system detects language consistent with self-harm or harm to others, we
        surface the 988 Suicide & Crisis Lifeline and the Crisis Text Line, and we open an internal
        escalation event so a human can follow up. We do not contact emergency services on your
        behalf — but we will always show you the resources.
      </p>

      <h2>Age</h2>
      <p>
        LessAlone is for adults aged 18 and over. We verify age at sign-up. We do not knowingly
        collect data from anyone under 18.
      </p>

      <h2>Your choices</h2>
      <ul>
        <li>
          <strong>Step away from a conversation</strong> any time, from within that conversation.
        </li>
        <li>
          <strong>Block a peer</strong> — ends that pairing and prevents future re-matching.
        </li>
        <li>
          <strong>Delete your account</strong> — anonymizes your profile and ends all active
          conversations. Some records (moderator actions, crisis events) are retained per the
          binding safety constraints described in our public architecture docs.
        </li>
      </ul>

      <h2>Where we run</h2>
      <p>
        Our infrastructure lives in the United States. Communications with our service are encrypted
        in transit (HTTPS). Data at rest is held in a managed Postgres database with access
        restricted to the operational team.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If we make material changes to how we handle your data, we'll notify active members via
        email and update the "Last updated" date above.
      </p>
    </LegalPage>
  );
}
